import { format } from "date-fns";
import writeXlsxFile from "write-excel-file";

// services
import { getRankingByConfiguration } from "../../../utils/services/ranking";

// utils
import {
  columnsForConfig,
  formatMatch,
  fileSlug,
  TOTAL_RANKING_CODE,
} from "./rankingConfig";

// Páginas buscadas de uma vez. O endpoint por configuração não tem teto de
// limit, e o do ranking geral é usado com 500 na tela de /event/ranking —
// 1000 corta pela metade o número de idas ao servidor sem pedir respostas
// grandes demais.
const PAGE_SIZE = 1000;

// Requisições simultâneas. O export de /event/ranking busca uma página por
// vez, e numa base grande o tempo total vira a soma de todas as respostas.
// Quatro em paralelo encurtam isso sem tratar a API como alvo de carga.
const CONCURRENCY = 4;

// Como cada coluna da tela vira célula da planilha. A chave é a mesma de
// RANKING_COLUMNS, então planilha e tabela nunca divergem: coluna escondida
// na tela (movimentos e tempo fora de MALA/PASSARELA) também não é exportada.
const EXPORT_CELLS = {
  loginId: { type: String, value: (row) => row?.loginId ?? null },
  name: { type: String, value: (row) => row?.name ?? null },
  totalPoints: { type: Number, value: (row) => row?.totalPoints ?? null },
  // mesma string da tela: "00:05 / 6", vazia sem partida registrada
  match: { type: String, value: (row) => formatMatch(row) || null },
  updatedAt: {
    type: String,
    value: (row) =>
      row?.updatedAt
        ? format(new Date(row.updatedAt), "dd-MM-yyyy HH:mm:ss")
        : null,
  },
};

// Monta o schema a partir das colunas visíveis, menos a de ações, que não
// tem conteúdo para exportar
export function buildSchema(rankingCode) {
  return columnsForConfig(rankingCode)
    .filter((column) => EXPORT_CELLS[column.key])
    .map((column) => ({ column: column.label, ...EXPORT_CELLS[column.key] }));
}

// Atualiza a barra do WaitExport. Os elementos só existem enquanto o modal
// está aberto, daí as guardas — sem elas, fechar o modal no meio da
// exportação derruba o processo com erro de null.
function updateProgress(done, total) {
  const percentage = Math.min(100, Math.round((done / total) * 100));
  const progressBar = document.querySelector(".export-progress");
  const percentageBar = document.querySelector(".export-percentage");

  if (progressBar) progressBar.style.width = `${percentage}%`;
  if (percentageBar) percentageBar.innerHTML = `${percentage}%`;
}

// `rankingCode` é o que está selecionado na tela e define as colunas e o nome
// do arquivo; `apiCode` é o que a api recebe — iguais, menos no total por
// categoria, em que a tela mostra um sentinela e a api recebe a lista de
// códigos. Sem apiCode, cai no rankingCode e o export segue como antes
export async function createXlsx({
  eventId,
  rankingCode,
  apiCode,
  totalLabel,
  search,
  setFinishExport,
}) {
  const schema = buildSchema(rankingCode);
  const code = apiCode || rankingCode;

  // mesma origem da tela, com os mesmos filtros: o que está listado é o que
  // vai para a planilha
  const fetchPage = (page) =>
    getRankingByConfiguration({
      eventId,
      code,
      offset: page,
      limit: PAGE_SIZE,
      search,
      columnSearch: "name",
    });

  // o total vem da PRIMEIRA RESPOSTA, não do countData da tela: aquele
  // corresponde à última busca concluída e, se o usuário digitar e exportar
  // antes do debounce, apontaria menos páginas do que o filtro enviado tem —
  // a planilha sairia truncada sem nenhum sinal
  const firstPage = await fetchPage(1);
  const total = Number(firstPage?.count) || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const pages = [firstPage];
  let done = 1;
  updateProgress(done, totalPages);

  // lotes de CONCURRENCY: o Promise.all preserva a ordem dentro do lote e os
  // lotes entram em sequência, então a planilha sai na ordem do ranking
  for (let start = 2; start <= totalPages; start += CONCURRENCY) {
    const batch = [];

    for (
      let page = start;
      page < start + CONCURRENCY && page <= totalPages;
      page += 1
    ) {
      batch.push(
        fetchPage(page).then((response) => {
          done += 1;
          updateProgress(done, totalPages);
          return response;
        }),
      );
    }

    const results = await Promise.all(batch);
    pages.push(...results);
  }

  const rows = pages.map((page) => page?.data ?? []).flat();

  // no total, o arquivo leva o nome da categoria ("ranking-games.xlsx"), que é
  // o mesmo rótulo do filtro; nas demais opções, o código da configuração
  const fileName =
    rankingCode === TOTAL_RANKING_CODE && totalLabel
      ? `${fileSlug(totalLabel)}.xlsx`
      : `ranking-${String(rankingCode).toLowerCase()}.xlsx`;

  await writeXlsxFile(rows, { schema, fileName });

  // só depois de o arquivo existir: avisar antes mostraria "finalizada" com a
  // planilha ainda sendo montada
  setFinishExport(true);

  return rows.length;
}
